import React, { useContext, useMemo, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Pane from 'components/Common/Pane'
import config from 'constants/config'
import isTimeLimited from 'lib/offer/isTimeLimited'
import OfferTimeLimitedText from '../OfferTimeLimitedText'
import { min, sum } from 'lib/array/arrayUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import { getBundleValueUpView } from 'lib/bundleAndSave/getBundleValueUpView'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import { OFFER_TYPE_HOTEL_SLUG } from 'constants/offer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import { BundleOfferPackageOptionView, getAvailableBundlePackages, areBundlePricesLoading } from 'selectors/offerPage/bundledOfferPageSelectors'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import GeoContext from 'contexts/geoContext'
import Group from 'components/utils/Group'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import * as Analytics from 'analytics/analytics'
import { useQsQueryParams } from 'hooks/useQueryParams'
const StyledOfferPartnershipLabel = styled(OfferPartnershipLabel)`
  margin-top: ${rem(12)};
`

const SummaryPane = styled(Pane)`
  position: relative;

  > * + * {
    margin-top: ${rem(12)};
  }
`

const PackagePrices = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${rem(4)};
  align-items: center;
`

const PriceDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const TaxAndFeesWrapper = styled.div`
  display: flex;
  align-items: center;
`

interface Props {
  offer: App.BundleOffer
  rooms: Array<App.Occupants>
  className?: string
  packageOptions: { [bundlePackageId: string]: BundleOfferPackageOptionView }
  onViewPackageOptions: () => void
  fetching?: boolean
}

function BundleAndSaveSummary(props: Props) {
  const {
    className,
    onViewPackageOptions,
    offer,
    rooms,
    fetching,
    packageOptions,
  } = props

  const { partnership } = useContext(GeoContext)

  const packageView = useMemo((): BundleOfferPackageOptionView => {
    const minPackageView = min(Object.values(packageOptions).filter((o) => o.capacityValid && o.hotelAvailable), (o) => o.hotelPrice)
    return minPackageView ?? Object.values(packageOptions)[0]
  }, [packageOptions])

  const allPackages = useMemo(() => {
    return (packageView?.options ?? []).map(p => p.package)
  }, [packageView])

  const duration = useMemo(() => {
    return sum(allPackages, (p) => p.duration)
  }, [allPackages])

  const {
    hotelPrice,
    propertyFees,
    taxesAndFees,
  } = packageView

  const showPrices = packageView.capacityValid && packageView.hotelAvailable
  const availableForPurchase = useIsAvailableForPurchase()
  const showIncludedGuestsExceeded = useMemo(() => allPackages.some((pkg) => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer.bundledOffers[pkg.offerId])), [allPackages, rooms, offer])
  const roomPolicyDescription = useMemo(() => allPackages.find((pkg) => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer.bundledOffers[pkg.offerId]))?.roomPolicyDescription, [allPackages, rooms, offer])

  const durationLabel = `${pluralizeToString('night', duration)}, 2 hotels from`

  const totalPrice = hotelPrice + (propertyFees ?? 0)
  const { showPriceDetails, shouldShowDiscountPercent, hotelDiscountPercent } = getBundleValueUpView(offer, packageView)
  const saleUnit = 'bundle'

  const shouldTrackPrice = !fetching && availableForPurchase && showPrices && !!totalPrice
  const priceTrackedRef = useRef(false)
  const { searchId } = useQsQueryParams<{ searchId?: string }>()
  useEffect(() => {
    if (shouldTrackPrice && searchId && offer?.id && !priceTrackedRef.current && !priceTrackedRef.current) {
      Analytics.trackClientEvent({
        subject: `searchId:${searchId}_offerId:${offer.id}`,
        action: totalPrice.toString(),
        category: 'price_accuracy',
        type: 'operational',
      })
      priceTrackedRef.current = true
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldTrackPrice, totalPrice, offer?.id])

  return (
    <SummaryPane border="neutral-six" borderRadius="XS" padding="28 20" shadow="bottom-small" className={className} data-testid="SummaryPane">
      <Group direction="vertical" gap={16}>
        {availableForPurchase && <>
          {showPrices &&
            <PackagePrices>
              {isTimeLimited(offer) && <OfferTimeLimitedText />}
              {shouldShowDiscountPercent && <PriceRowValueDiscount
                type="estimate"
                discountPercentage={hotelDiscountPercent}
              />}
              <PriceDetails>
                <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
                <PriceRowPrice
                  data-testid="pricing"
                  size="L"
                  price={totalPrice}
                  saleUnit={saleUnit}
                />
                <TaxAndFeesWrapper>
                  <PriceRowTaxesAndFees
                    endIcon={showPriceDetails && <OfferPriceDetailsRow
                      offerPage
                      offer={offer}
                      duration={duration}
                      propertyFees={propertyFees}
                      hotelPrice={hotelPrice}
                      taxesAndFees={taxesAndFees}
                      rooms={rooms}
                      extraGuestMessage={showIncludedGuestsExceeded ? roomPolicyDescription : ''}
                      dueAtPropertyMessage={offer.property.taxesAndFeesContent ? offer.property.taxesAndFeesContent : ''}
                      trigger="info-icon"
                      cta={
                        <TextButton
                          kind="primary"
                          fit="flex"
                        >
                          View {config.brandConfig.travelSynonym} options
                        </TextButton>
                      }
                      onCtaAfterClose={onViewPackageOptions}
                    />}
                  />
                </TaxAndFeesWrapper>
                {showIncludedGuestsExceeded && (
                  <AdditionalGuestsPopup
                    complex={false}
                    description="+ Extra guest surcharge"
                    modalContent={roomPolicyDescription}
                  />
                )}
              </PriceDetails>
              { partnership && partnership.code !== 'velocity' && <StyledOfferPartnershipLabel offer={offer} /> }
              <LoadingIndicator floating backdrop="grey-light" opaque visible={!!fetching} />
            </PackagePrices>}
          <TextButton
            kind="primary"
            data-testid="OfferAvailable"
            onClick={onViewPackageOptions}
            fit="flex"
          >
            View Bundles
          </TextButton>
        </>}

        {!availableForPurchase &&
          <>
            <BodyText
              variant="large"
              weight="bold"
              data-testid="OfferSoldOut"
            >
              This offer has SOLD OUT
            </BodyText>
            <TextButton
              kind="primary"
              to={`/${OFFER_TYPE_HOTEL_SLUG}`}
              fit="flex"
            >
              Browse all hotel offers
            </TextButton>
          </>}
      </Group>
    </SummaryPane>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  packageOptions: getAvailableBundlePackages(state, appState),
  rooms: state.rooms,
  offer: state.offer,
  fetching: areBundlePricesLoading(appState, state),
}), BundleAndSaveSummary)
